import { DestroyRef, Injector, inject } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { CrudFormServiceFactory } from '@common/crud/frontend-shared';
import { AppMessageFactory, ItemServiceFactory, ModelActionService, ModelColumnService, PageFacade, RestraintsFactory, getCurrentRouteState, takeWhileAlive } from '@core/frontend-shared';
import { getItemModelText } from '@core/shared/model';
import { isConstructor } from '@core/shared/utils';
import { BehaviorSubject, Observable, filter, map, switchMap } from 'rxjs';
import { CrudControllerFactory, CrudListController } from '../controller';
import { crudManagerDefaults } from '../crud.config';
import { CrudManagerUtils, isCrudModeWithRouteParam } from './crud-manager.utils';
import { CRUD_ITEM_VIEW_SERVICE_TOKEN } from '../crud-editor';
import * as i0 from "@angular/core";
import * as i1 from "@core/frontend-shared";
import * as i2 from "@common/crud/frontend-shared";
import * as i3 from "../controller";
// NOTE: provided in CrudManager2Component
export let CrudManagerService = /*#__PURE__*/(() => {
  class CrudManagerService {
    get Model() {
      return this.Model$.getValue();
    }
    get restraints() {
      return this._restraints || this.createRestraints();
    }
    get crudService() {
      return this.service.crud;
    }
    get enableEdit() {
      return this.config.list.editable;
    }
    get enableDelete() {
      return this.config.list.deleteable;
    }
    get enableToggle() {
      return this.config.list.toggleable;
    }
    get enableCreate() {
      return this.config.list.createable;
    }
    constructor(modelActionService, restraintsFactory, itemServiceFactory, page, formServiceFactory, modelColumnService, controllerFactory, messages, injector) {
      this.modelActionService = modelActionService;
      this.restraintsFactory = restraintsFactory;
      this.itemServiceFactory = itemServiceFactory;
      this.page = page;
      this.formServiceFactory = formServiceFactory;
      this.modelColumnService = modelColumnService;
      this.controllerFactory = controllerFactory;
      this.messages = messages;
      this.injector = injector;
      this.destroyRef = inject(DestroyRef);
      this.configurated = false;
      this.Model$ = new BehaviorSubject(null);
      this.templates = {};
      // handleRouteChange will extract information from route and build crudRouteState from that
      this.crudRouteState$ = new BehaviorSubject(null);
      // after crudRouteState emits, (possibly async) updateModeAndItem will execute and solve modeAndItem
      this.modeAndItem$ = new BehaviorSubject({
        mode: null,
        item: null
      });
      this.contextGroups$ = this.modeAndItem$.pipe(takeWhileAlive(this.destroyRef), map(({
        mode
      }) => {
        return CrudManagerUtils.getContextGroups(mode);
      }));
      this.modifiers = {
        formService: null
      };
    }
    configurate(config, Model, customItemService, templates, router, baseRoute) {
      // injecting crudItemViewService here is required to ensure it is created before CrudManager emits first status update.
      const itemViewService = this.injector.get(CRUD_ITEM_VIEW_SERVICE_TOKEN);
      this.config = {
        layout: config.layout || crudManagerDefaults.layout,
        list: {
          ...crudManagerDefaults.list,
          ...config.list
        },
        editor: {
          ...crudManagerDefaults.editor,
          ...config.editor
        },
        pageTitle: {
          ...crudManagerDefaults.pageTitle,
          ...config.pageTitle
        },
        batch: config.batch || null,
        dependencies: config.dependencies,
        itemActions: config.itemActions
      };
      if (this.config.list.loadAllItems) this.config.list.itemsPerPage = false;
      if (this.config.layout.name === 'list') {
        this.config.editor.showBackButton = false;
      }
      this.configurated = true;
      this.router = router;
      this.baseRoute = baseRoute;
      this.templates = templates;
      this.Model$.next(Model);
      this.setupItemService(customItemService);
      this.createRestraints();
    }
    afterInitialize() {
      // routes may trigger crud requests which require pathParams to have been set before.
      // so the CrudManager must have a chance to trigger custom initializer initializeCrudManager() before binding to route changes. 
      this.subscribeToRouteChanges(this.router, this.baseRoute);
      this.subscribeToRouteStateChanges();
    }
    pauseCrudStateEvents(enablePaused) {
      this.crudService.pauseCrudStateEvents(enablePaused);
      if (enablePaused === false && this.getCurrentModeAndItem().item) {
        // auto-refresh item when ending pause
        this.reload(true);
      }
    }
    reload(onlyCurrentItem = false) {
      if (onlyCurrentItem) {
        const state = this.crudRouteState$.getValue();
        if (state.id) {
          this.getItemController(true).loadItem(state.id).subscribe({
            next: item => {
              const mode = this.modeAndItem$.getValue().mode;
              this.applyModeAndItem({
                mode,
                item
              });
            }
          });
        }
      } else {
        // ItemService will update observables bubbling up to the crudComponents
        this.service.refreshAllItems();
      }
    }
    // #######  List Controller  ###########################################################
    /**
     * ListControllers are used in different scenarios.
     * When used in a CRUD UI system, it is easiest to let CrudManagerService construct one.
     * But some components may be used outside of CRUD UI.
     * It is fine to create a ListController instance manually then.
     * Depending on situation, the CrudManagerService can construct a ListController or store an existing one.
     */
    getListController(createIfNotExisting = true) {
      if (!this.listController && createIfNotExisting) {
        this.listController = new CrudListController(this.config.list, this.service, getCurrentRouteState(), this.modelColumnService, this.config.dependencies);
        this.contextGroups$.pipe(takeWhileAlive(this.destroyRef)).subscribe(contextGroups => {
          this.listController.setContextGroups(contextGroups);
        });
      }
      return this.listController || null;
    }
    setListController(controller) {
      this.listController = controller;
    }
    // #######  Item Controller  ###########################################################
    getItemController(createIfNotExisting = true) {
      if (!this.itemController && createIfNotExisting) {
        if (!this.configurated) {
          throw new Error('called getItemController before CrudManager was configurated!');
        }
        this.itemController = this.controllerFactory.createItemController(this.service, this.restraints);
      }
      return this.itemController || null;
    }
    executeItemAction(item, action) {
      return this.getItemController(true).executeItemAction(item, action);
    }
    // #######  Route management  ###########################################################
    subscribeToRouteChanges(router, route) {
      router.events.pipe(filter(e => e instanceof NavigationEnd), takeWhileAlive(this.destroyRef)).subscribe(e => {
        // this check indicated the child route is in a temporary state.
        // another NavigationEnd event will fire right after this.
        // if(route.firstChild?.component === null) return;
        this.handleRouteChange(route);
      });
      this.handleRouteChange(route);
    }
    handleRouteChange(route) {
      if (route.component === null) {
        // firstChild route has no component assigned, this can happen when using an "empty" route for resolvers
        route = route.firstChild;
      }
      const hasChildRoute = !!route?.firstChild;
      if (!hasChildRoute) {
        this.crudRouteState$.next({
          type: 'list',
          url: '',
          id: null,
          route
        });
      } else {
        const child = route.firstChild;
        // console.log('chzild route',child)
        const snapshot = child.snapshot || child._futureSnapshot;
        const url = snapshot.url[0].path;
        const mode = snapshot.data.mode;
        if (!mode) {
          // can happen when there are custom child routes
          this.crudRouteState$.next({
            type: 'custom',
            url,
            id: null,
            route
          });
        } else {
          // route is child route with configurated mode, extract itemId if needed
          // TODO: ID may be stored in other parameter than :id!
          const activeModeUsesItemId = ['edit', 'clone', 'view'].includes(mode);
          if (activeModeUsesItemId) {
            const idParamName = snapshot.data.routeIdParamName || 'id';
            const id = parseInt(snapshot.params[idParamName], 10);
            this.crudRouteState$.next({
              type: mode,
              url,
              id,
              route
            });
          } else {
            this.crudRouteState$.next({
              type: mode,
              url,
              id: null,
              route
            });
          }
        }
      }
    }
    subscribeToRouteStateChanges() {
      const updatePageTitle = state => {
        const titleConfig = this.config.pageTitle;
        const itemViewHasOwnTitles = titleConfig.setPageTitleForItemView;
        if (state.type === 'list' || !itemViewHasOwnTitles) {
          const title = this.getItemText('items') + ' verwalten';
          if (titleConfig.customPageTitle) {
            this.setPageTitle(titleConfig.customPageTitle(state.type) || title);
          } else {
            this.setPageTitle(title);
          }
        }
      };
      this.crudRouteState$.pipe(takeWhileAlive(this.destroyRef), filter(state => !!state)).subscribe(state => {
        this.configurateModeAndItem(state);
        if (this.config.pageTitle.setPageTitleForListView) updatePageTitle(state);
      });
    }
    configurateModeAndItem(state) {
      if (state.type === 'list') {
        this.applyModeAndItem(null);
      } else if (state.type === 'custom') {
        this.applyModeAndItem(null);
      } else if (state.type === 'batch' || state.route.routeConfig.path === 'batch') {
        this.applyModeAndItem({
          mode: 'batch',
          item: null
        });
      } else if (state.type === 'new' || state.route.routeConfig.path === 'new') {
        const item = this.getItemController(true).newItem();
        this.applyModeAndItem({
          mode: 'new',
          item
        });
      } else if (state.id) {
        this.getItemController(true).loadItem(state.id).subscribe({
          next: item => {
            this.applyModeAndItem({
              mode: state.type,
              item
            });
          },
          error: () => {
            this.messages.create(getItemModelText('itemNotExists', {
              id: state.id
            })).setSeverity('warn');
            this.router.navigate(['../'], {
              relativeTo: state.route
            });
          }
        });
      } else {
        console.warn(state);
        throw new Error('CrudManager routeStateChange handler was unable to understand Route Configuration!');
      }
    }
    applyModeAndItem(patch) {
      if (patch === null) {
        // this means we've entered list or custom view
        return;
      }
      if (patch.mode === 'list') throw new Error('CrudManagerService.applyModeAndItem should not be called explicitly for list, only for ItemEditModes!');
      if (patch.mode === null) {
        this.modeAndItem$.next({
          mode: null,
          item: null
        });
      } else {
        if (!IS_PRODUCTION) {
          const modeChanged = !!patch.mode;
          if (patch.item && !(patch.item instanceof this.Model)) {
            throw new Error('invalid data passed to applyModeAndItem: Item is no instance of configurated Model!');
          }
          if (modeChanged && typeof patch.item === 'undefined') {
            throw new Error('invalid data passed to applyModeAndItem: when setting a mode, an explicit value for item (either data or null) must be passed!');
          }
        }
        const prev = this.modeAndItem$.getValue();
        const next = {
          ...prev,
          ...patch
        };
        this.modeAndItem$.next(next);
      }
    }
    navigate(commands) {
      this.router.navigate(commands, {
        relativeTo: this.baseRoute
      });
    }
    // TODO: this could replace manual navigation inside CrudEditor.subscribeToFormService
    navigateTo(requestedView, param) {
      const currentRoute = this.crudRouteState$.getValue();
      const requestedViewUsesParams = isCrudModeWithRouteParam(requestedView);
      // nothing to do, we are already in correct view!
      if (!requestedViewUsesParams && currentRoute.type === requestedView) {
        return;
      }
      switch (requestedView) {
        case 'list':
          this.router.navigate(['./'], {
            relativeTo: this.baseRoute
          });
          break;
        case 'new':
        case 'batch':
          this.router.navigate(['./', requestedView], {
            relativeTo: this.baseRoute
          });
          break;
        case 'edit':
        case 'view':
          if (!param) throw new Error('navigateTo ' + requestedView + ' requires route param to be passed (item ID)!');
          this.router.navigate(['./', param], {
            relativeTo: this.baseRoute
          });
          break;
        case 'clone':
          if (!param) throw new Error('navigateTo ' + requestedView + ' requires route param to be passed (item ID)!');
          this.router.navigate(['./clone', param], {
            relativeTo: this.baseRoute
          });
          break;
      }
    }
    executeCustomButtonAction(event, btn) {
      if (btn.command) {
        btn.command(event);
      } else if (btn.routerLink) {
        this.navigate(btn.routerLink);
      } else {
        throw new Error('dont know how to handle clicking custom button');
      }
    }
    // #######  Editor View setup  ###########################################################
    setupItemService(customItemServiceInput) {
      let itemServiceInstance;
      if (customItemServiceInput) {
        if (isConstructor(customItemServiceInput)) {
          const instance = this.itemServiceFactory.useDestroyRef(this.destroyRef).createCustomItemService(customItemServiceInput, this.getModel());
          itemServiceInstance = instance;
        } else {
          itemServiceInstance = customItemServiceInput;
        }
      } else {
        const usePagedItemService = this.config.list.loadAllItems === false && this.config.list.itemsPerPage !== false;
        if (!usePagedItemService) {
          itemServiceInstance = this.itemServiceFactory.useDestroyRef(this.destroyRef).createSimpleItemService(this.getModel());
        } else {
          itemServiceInstance = this.itemServiceFactory.useDestroyRef(this.destroyRef).createPagedItemService(this.getModel());
          itemServiceInstance.initialize(this.config.list.itemsPerPage || 20);
        }
      }
      this.service = itemServiceInstance;
      if (!this.config.list.useCrudEndpoint) throw new Error('CrudManager error: Please set config.list.useCrudEndpoint correctly.');
      const useCrudEndpoint = this.config.list.useCrudEndpoint;
      this.service.useCrudEndpoint(useCrudEndpoint);
      this.bindToCrudServiceEvents();
    }
    bindToCrudServiceEvents() {
      /**
       * subscribing to CrudService events manually for reloading items is not required anymore.
       * CrudService will emit events to CrudRequestState, ItemService will listen to these and trigger reloads where needed.
       */
    }
    // #######  Restraints  ###########################################################
    createRestraints() {
      this._restraints = this.restraintsFactory.createForModel(this.Model);
      return this._restraints;
    }
    getItemRestraintsInfo(item) {
      if (item) {
        const r = this.restraints;
        return {
          canEdit: r.canUpdateItem(item),
          canDelete: r.canRemoveItem(item),
          canToggle: r.canToggleItem(item)
        };
      } else {
        return {
          canEdit: null,
          canDelete: null,
          canToggle: null
        };
      }
    }
    // #######  Item Actions  ###########################################################
    getItemActions$(forSpecificMode) {
      if (forSpecificMode) {
        return this._getItemActionsForMode(forSpecificMode);
      } else {
        return this.modeAndItem$.pipe(takeWhileAlive(this.destroyRef), switchMap(modeAndItem => {
          return this._getItemActionsForMode(modeAndItem.mode);
        }));
      }
    }
    _getItemActionsForMode(mode) {
      return this.Model$.pipe(filter(Model => !!Model), switchMap(Model => {
        return this.modelActionService.getModelActions(Model, mode, CrudManagerUtils.getContextGroups(mode), this.config?.itemActions || []);
      }));
    }
    // #######  Utils  ###########################################################
    getModel() {
      return this.Model;
    }
    getCurrentModeAndItem() {
      return this.modeAndItem$.getValue();
    }
    setPageTitle(title) {
      this.page.setTitle(title);
    }
    getBaseRoute() {
      return this.baseRoute;
    }
    setFormServiceCustomizer(func) {
      this.modifiers.formService = func;
    }
    getItemText(id) {
      const item = this.Model || null;
      if (!item) return id;
      return getItemModelText(id, item);
    }
    static {
      this.ɵfac = function CrudManagerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CrudManagerService)(i0.ɵɵinject(i1.ModelActionService), i0.ɵɵinject(i1.RestraintsFactory), i0.ɵɵinject(i1.ItemServiceFactory), i0.ɵɵinject(i1.PageFacade), i0.ɵɵinject(i2.CrudFormServiceFactory), i0.ɵɵinject(i1.ModelColumnService), i0.ɵɵinject(i3.CrudControllerFactory), i0.ɵɵinject(i1.AppMessageFactory), i0.ɵɵinject(i0.Injector));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: CrudManagerService,
        factory: CrudManagerService.ɵfac
      });
    }
  }
  return CrudManagerService;
})();